import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { listMyDisputes, createDispute } from '../../services/disputes'
import { listCenterOrders } from '../../services/orders'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'

const CenterDisputesPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [disputes, setDisputes] = useState([])
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')
  const [formData, setFormData] = useState({
    orderId: '',
    subject: '',
    message: ''
  })
  const [submitting, setSubmitting] = useState(false)

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center' }
  ]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    
    // Fetch disputes
    const disputesRes = await listMyDisputes()
    if (disputesRes.status === 200) {
      setDisputes(disputesRes.data || [])
    }

    // Fetch orders for the create dispute form
    const ordersRes = await listCenterOrders()
    if (ordersRes.status === 200) {
      setOrders(ordersRes.data || [])
    }

    setLoading(false)
  }

  const handleCreateDispute = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setMessage('')

    if (!formData.orderId || !formData.subject || !formData.message) {
      setMessage('Please fill in all required fields')
      setMessageType('error')
      setSubmitting(false)
      return
    }

    const res = await createDispute(formData)
    if (res.status === 200) {
      setMessage('Dispute created successfully')
      setMessageType('success')
      setFormData({ orderId: '', subject: '', message: '' })
      setShowCreateModal(false)
      fetchData()
    } else {
      setMessage(res.error || 'Failed to create dispute')
      setMessageType('error')
    }
    setSubmitting(false)
  }

  const filteredDisputes = disputes.filter(dispute => {
    // Filter by status
    let passesFilter = true
    if (filter !== 'ALL') {
      passesFilter = dispute.status === filter
    }

    // Filter by search query
    if (searchQuery && passesFilter) {
      const query = searchQuery.toLowerCase()
      const matchesSubject = dispute.subject?.toLowerCase().includes(query)
      const matchesMessage = dispute.message?.toLowerCase().includes(query)
      const matchesOrderId = dispute.orderId?._id?.toLowerCase().includes(query)

      return matchesSubject || matchesMessage || matchesOrderId
    }

    return passesFilter
  })

  const openDisputes = disputes.filter(d => d.status === 'open')
  const resolvedDisputes = disputes.filter(d => d.status === 'resolved')

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'disputes'

              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">Disputes</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Disputes</h1>
              <p className="text-slate-600 mt-1">Manage and track order disputes</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <PlusCircleIcon className="w-5 h-5" />
              Create Dispute
            </button>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-6 p-4 rounded-xl border ${
              messageType === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-center gap-2">
                {messageType === 'success' ? (
                  <CheckCircleIcon className="w-5 h-5" />
                ) : (
                  <ExclamationTriangleIcon className="w-5 h-5" />
                )}
                <p className="font-medium">{message}</p>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-emerald-100 text-sm font-medium">Total Disputes</p>
                <ExclamationTriangleIcon className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-3xl font-bold">{disputes.length}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-yellow-100 text-sm font-medium">Open</p>
                <ClockIcon className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-3xl font-bold">{openDisputes.length}</p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-green-100 text-sm font-medium">Resolved</p>
                <CheckCircleIcon className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-3xl font-bold">{resolvedDisputes.length}</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by subject, message, or order ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="mt-2 text-sm text-slate-600">
                Found {filteredDisputes.length} dispute{filteredDisputes.length !== 1 ? 's' : ''} matching "{searchQuery}"
              </p>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {['ALL', 'open', 'resolved'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  filter === status
                    ? 'bg-emerald-600 text-white shadow-lg'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {status === 'ALL' ? 'All Disputes' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Disputes List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
                <p className="mt-4 text-slate-600">Loading disputes...</p>
              </div>
            </div>
          ) : filteredDisputes.length === 0 ? (
            <div className="text-center py-24 bg-white rounded-2xl border border-slate-200 border-dashed">
              <ExclamationTriangleIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">
                {searchQuery ? 'No disputes found' : 'No disputes yet'}
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery ? 'Try adjusting your search criteria' : 'Disputes will appear here when you create them'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Create Dispute
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredDisputes.map((dispute) => (
                <div
                  key={dispute._id}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-slate-900">{dispute.subject}</h3>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          dispute.status === 'open'
                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                            : 'bg-green-100 text-green-700 border border-green-200'
                        }`}>
                          {dispute.status === 'open' ? 'Open' : 'Resolved'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{dispute.message}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <ShoppingBagIcon className="w-4 h-4" />
                          <span>Order: #{dispute.orderId?._id?.slice(-8) || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{new Date(dispute.createdAt).toLocaleDateString()}</span>
                        </div>
                        {dispute.resolvedAt && (
                          <div className="flex items-center gap-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>Resolved: {new Date(dispute.resolvedAt).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {dispute.resolutionNotes && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-semibold text-green-900 mb-1">Resolution Notes:</p>
                      <p className="text-sm text-green-800">{dispute.resolutionNotes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Create Dispute Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-slate-900">Create Dispute</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setMessage('')
                  setFormData({ orderId: '', subject: '', message: '' })
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCreateDispute} className="p-6 space-y-6">
              {/* Order Selection */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Order <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.orderId}
                  onChange={(e) => setFormData({ ...formData, orderId: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                >
                  <option value="">Select an order</option>
                  {orders.map(order => (
                    <option key={order._id} value={order._id}>
                      Order #{order.paymentTxRef?.slice(-8) || order._id.slice(-8)} - 
                      {order.eventId?.title || order.offerId?.title || 'N/A'} - 
                      {order.currency} {order.total?.toLocaleString()}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Select the order you want to dispute</p>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief description of the issue"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  maxLength={200}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">{formData.subject.length}/200 characters</p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Provide detailed information about the dispute..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[150px]"
                  maxLength={5000}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">{formData.message.length}/5000 characters</p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setMessage('')
                    setFormData({ orderId: '', subject: '', message: '' })
                  }}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <ChatBubbleLeftRightIcon className="w-5 h-5" />
                      Create Dispute
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CenterDisputesPage

